import { NavMenu } from "@shopify/app-bridge-react";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import agent from "../api/agent";
import { useStore } from "../stores/store";
import {
    FITMENT_CATALOG_LABEL_CHANGED,
    getCachedFitmentCatalogLabel,
    getStoredFitmentCatalogLabel,
    setCachedFitmentCatalogLabel,
} from "../utils/fitmentCatalogLabel";
import { isProductGuideEnabledForCurrentContext } from "../utils/productGuideFeature";
import { isServiceBookingEnabledForShop } from "../utils/serviceBookingFeature";
import { useLegacyMode } from "../../features/legacy/LegacyModeContext";
import { nativeNavigation } from './nativeNavigation';

const AppNavMenu = () => {
    const { userStore } = useStore();
    const { loading, isLegacy } = useLegacyMode();
    const [vehicleLabel, setVehicleLabel] = useState(() => getStoredFitmentCatalogLabel() ?? "");
    // Temporary rollout gate: Trail Ops staging can see the guide builder during development.
    const showProductGuides = isProductGuideEnabledForCurrentContext(userStore.user?.shopDomain);
    const showServiceBooking = isServiceBookingEnabledForShop(userStore.user?.shopDomain);

    useEffect(() => {
        if (loading || isLegacy) return;

        let cancelled = false;

        const loadVehicleLabel = async () => {
            try {
                const catalog = await agent.Settings.fitmentCatalog();
                if (!cancelled) {
                    setVehicleLabel(setCachedFitmentCatalogLabel(catalog.name));
                }
            } catch {
                if (!cancelled) setVehicleLabel(getCachedFitmentCatalogLabel());
            }
        };

        const handleLabelChange = (event: Event) => {
            const nextLabel = (event as CustomEvent<string>).detail;
            if (nextLabel) setVehicleLabel(nextLabel);
        };

        window.addEventListener(FITMENT_CATALOG_LABEL_CHANGED, handleLabelChange);
        loadVehicleLabel();
        return () => {
            cancelled = true;
            window.removeEventListener(FITMENT_CATALOG_LABEL_CHANGED, handleLabelChange);
        };
    }, [isLegacy, loading]);

    // Do not register native links until the merchant mode is known. Legacy
    // merchants use the in-app horizontal navigation instead.
    if (loading || isLegacy) return null;

    return (
        <NavMenu>
            {nativeNavigation(vehicleLabel || '\u00a0', showProductGuides, showServiceBooking).map(link =>
                <a key={link.to} href={link.to} rel={link.to === '/dashboard' ? 'home' : undefined}>{link.label}</a>)}
        </NavMenu>
    );
};

export default observer(AppNavMenu);
